import { CsvEnum, Id, IsoDate, IsoDateTime, ListQuery, Paginated, UploadSpec, UploadTicket, VAT_CODES, defineAction, defineEvent, updateFields, z } from '@mkcore/shared';
import { proposalPermissions as P } from './permissions';

// Namespace proposal (docs/04-domeinen/proposals.md): proposals with versions, lines, options and alternatives (CO-0595),
// the decision of the client that wins the lead (CO-0594).

export const PROPOSAL_TYPES = ['fixed_price', 'time_and_materials', 'support', 'research'] as const;
export const PROPOSAL_STATUSES = ['draft', 'sent', 'accepted', 'rejected', 'expired', 'cancelled'] as const;
export const VERSION_STATUSES = ['draft', 'sending', 'sent', 'accepted', 'rejected', 'superseded', 'expired'] as const;
export const LINE_KINDS = ['item', 'option', 'alternative'] as const;
export const LINE_UNITS = ['hour', 'day', 'piece', 'month', 'fixed'] as const;
export const PROPOSAL_LANGUAGES = ['nl', 'en'] as const;
/** A proposal is valid this many days after it is sent, unless the version says otherwise. */
export const DEFAULT_VALID_DAYS = 30;
export const ProposalType = z.enum(PROPOSAL_TYPES);
export const ProposalStatus = z.enum(PROPOSAL_STATUSES);
export const VersionStatus = z.enum(VERSION_STATUSES);

const ExpectedVersion = z.number().int().positive().optional().describe('Version you read; the update fails if it changed');
const Cents = z.number().int().min(0).max(2_000_000_000);

export const ProposalLine = z
  .object({
    id: Id,
    position: z.number().int(),
    kind: z.enum(LINE_KINDS).describe('item: always in the total; option: only when chosen; alternative: one of its group'),
    groupKey: z.string().nullable().describe('The group of alternatives that exclude each other'),
    selected: z.boolean().describe('For an option or alternative: chosen (by default, or by the client)'),
    phase: z.string().nullable().describe('The phase or milestone, for the start planning (CO-0594)'),
    description: z.string(),
    quantity: z.number(),
    unit: z.enum(LINE_UNITS),
    unitPriceCents: z.number().int(),
    amountCents: z.number().int().describe('round(quantity × unit price)'),
    vatCode: z.enum(VAT_CODES),
    vatRateBp: z.number().int(),
    hours: z.number().nullable().describe('Expected hours, also at a fixed price'),
  })
  .openapi('ProposalLine');

export const MailWarning = z
  .object({
    code: z.enum(['amount-unknown', 'emoji']).describe('amount-unknown: a euro amount that is no line, unit price or total of the version'),
    detail: z.string(),
  })
  .openapi('ProposalMailWarning');

export const ProposalVersion = z
  .object({
    id: Id,
    proposalId: Id,
    version: z.number().int(),
    status: VersionStatus,
    intro: z.string(),
    scope: z.string(),
    terms: z.string(),
    validUntil: IsoDate.nullable(),
    subtotalCents: z.number().int().describe('Items and the chosen options and alternatives'),
    vatCents: z.number().int(),
    totalCents: z.number().int(),
    pdfFileId: Id.nullable().describe('The rendered PDF in files, from the proposal-builder'),
    renderedAt: IsoDateTime.nullable(),
    contentHash: z.string().describe('SHA-256 over text, lines, PDF and the own mail'),
    mailSubject: z.string().nullable().describe('The own mail (CO-1364): subject; null is the standard subject'),
    mailBody: z.string().nullable().describe('The own mail in markdown; null is the standard text'),
    mailTo: z.array(z.string()).nullable().describe('Recipients; null is the contact of the proposal'),
    mailCc: z.array(z.string()).nullable(),
    mailWarnings: z.array(MailWarning).describe('Amounts in the own mail that are not in the version, and emoji; shown in the approval'),
    sentAt: IsoDateTime.nullable(),
    sendError: z.string().nullable().describe('Why the last send of this version failed, such as a mailbox that refuses (#273); null when it did not. Sending it again retries it'),
    decidedAt: IsoDateTime.nullable(),
    viewedAt: IsoDateTime.nullable().describe('The first time the client opened it in the portal (CO-0663)'),
    decidedVia: z.enum(['person', 'portal']).nullable().describe('portal: the client decided itself in the portal (CO-0663)'),
    decidedByName: z.string().nullable().describe('The name the client typed, or the person who recorded the decision'),
    decidedIp: z.string().nullable().describe('The address of a decision in the portal'),
    lines: z.array(ProposalLine),
    rowVersion: z.number().int().describe('The version of the row, for If-Match'),
  })
  .openapi('ProposalVersion');

export const Proposal = z
  .object({
    id: Id,
    number: z.string().describe('MKC-<KLANT>-<JAAR>-<NNN>'),
    organisationId: Id,
    organisationName: z.string(),
    contactId: Id.nullable(),
    contactName: z.string().nullable(),
    leadId: Id.nullable(),
    title: z.string(),
    type: ProposalType.describe('fixed_price, time_and_materials, support or research'),
    language: z.enum(PROPOSAL_LANGUAGES),
    currency: z.string(),
    status: ProposalStatus,
    currentVersion: z.number().int().describe('The number of the latest version'),
    totalCents: z.number().int().describe('The total of the latest version'),
    validUntil: IsoDate.nullable(),
    sentAt: IsoDateTime.nullable(),
    decidedAt: IsoDateTime.nullable(),
    decisionNote: z.string().nullable(),
    engagementId: Id.nullable(),
    projectId: Id.nullable(),
    ownerId: Id,
    ownerName: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    version: z.number().int(),
    url: z.string(),
  })
  .openapi('Proposal');

export const ProposalDetail = Proposal.extend({ versions: z.array(ProposalVersion).describe('Newest first; the first is the current one') }).openapi('ProposalDetail');

export const listProposals = defineAction({
  name: 'proposal.proposal.list',
  method: 'get',
  path: '/proposal/proposals',
  permission: P.keys.read,
  input: ListQuery.extend({
    q: z.string().max(200).optional().describe('Search in number and title'),
    status: CsvEnum(PROPOSAL_STATUSES).optional(),
    organisationId: Id.optional(),
    leadId: Id.optional(),
    ownerId: Id.optional(),
  }),
  output: Paginated(Proposal),
  summary: 'Proposals, newest first, with number, status and the total of the latest version (CO-0595)',
});

export const getProposal = defineAction({
  name: 'proposal.proposal.get',
  method: 'get',
  path: '/proposal/proposals/{id}',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: ProposalDetail,
  etag: true,
});

export const createProposal = defineAction({
  name: 'proposal.proposal.create',
  method: 'post',
  path: '/proposal/proposals',
  permission: P.keys.create,
  input: z
    .object({
      organisationId: Id.optional().describe('The client; default the organisation of the lead'),
      leadId: Id.optional().describe('The lead: client, contact and title are taken from it'),
      contactId: Id.nullable().optional(),
      title: z.string().trim().min(1).max(300).optional().describe('Default the title of the lead'),
      type: ProposalType,
      language: z.enum(PROPOSAL_LANGUAGES).optional().describe('Default the invoice language of the organisation'),
      ownerId: Id.optional(),
    })
    .strict(),
  output: ProposalDetail,
  summary: 'Make a proposal with version 1 for an organisation or from a lead; the number follows MKC-<KLANT>-<JAAR>-<NNN> (CO-0595)',
  description: 'The organisation needs a client code (org.organisation.update code); without one: organisation-code-missing.',
});

export const updateProposal = defineAction({
  name: 'proposal.proposal.update',
  method: 'patch',
  path: '/proposal/proposals/{id}',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      title: z.string().trim().min(1).max(300).optional(),
      type: ProposalType.optional(),
      language: z.enum(PROPOSAL_LANGUAGES).optional(),
      contactId: Id.nullable().optional(),
      ownerId: Id.optional(),
      engagementId: Id.nullable().optional().describe('The engagement made from the accepted proposal (CO-0594)'),
      projectId: Id.nullable().optional().describe('The project made from the accepted proposal (CO-0594)'),
      expectedVersion: ExpectedVersion,
    })
    .strict(),
  output: ProposalDetail,
  etag: true,
});

export const cancelProposal = defineAction({
  name: 'proposal.proposal.cancel',
  method: 'post',
  path: '/proposal/proposals/{id}/cancel',
  permission: P.keys.update,
  input: z.object({ id: Id, note: z.string().max(2000).optional() }).strict(),
  output: ProposalDetail,
  successStatus: 200,
  summary: 'Withdraw a proposal that is not decided; its number stays',
});

export const createVersion = defineAction({
  name: 'proposal.version.create',
  method: 'post',
  path: '/proposal/proposals/{id}/versions',
  permission: P.keys.update,
  input: z.object({ id: Id }).strict(),
  output: ProposalDetail,
  summary: 'A new version: a copy of the latest, which is then superseded; for changes after sending or a rejection',
});

export const updateVersion = defineAction({
  name: 'proposal.version.update',
  method: 'patch',
  path: '/proposal/versions/{id}',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      intro: z.string().max(50_000).optional(),
      scope: z.string().max(50_000).optional(),
      terms: z.string().max(50_000).optional(),
      validUntil: IsoDate.nullable().optional().describe('Default 30 days after sending'),
      mailSubject: z.string().trim().min(1).max(300).nullable().optional().describe('The own mail (CO-1364): subject; null for the standard subject'),
      mailBody: z.string().max(20_000).nullable().optional().describe('The own mail in markdown, the whole text the client gets; null for the standard text'),
      mailTo: z.array(z.email()).min(1).max(10).nullable().optional().describe('Recipients of the mail; null for the contact of the proposal'),
      mailCc: z.array(z.email()).max(10).nullable().optional(),
      expectedVersion: ExpectedVersion,
    })
    .strict(),
  output: ProposalVersion,
  etag: true,
  summary: 'Change the text, the validity or the own mail of a draft version',
});

const lineFields = {
  description: z.string().trim().min(1).max(2000),
  kind: z.enum(LINE_KINDS).optional().describe('Default item'),
  groupKey: z.string().trim().min(1).max(40).nullable().optional().describe('For an alternative: the group whose lines exclude each other'),
  selected: z.boolean().optional().describe('For an option or alternative: chosen by default'),
  phase: z.string().trim().max(200).nullable().optional(),
  quantity: z.number().min(0).max(1_000_000).optional().describe('Default 1'),
  unit: z.enum(LINE_UNITS).optional().describe('Default fixed'),
  unitPriceCents: Cents,
  vatCode: z.enum(VAT_CODES).optional().describe('Default from the VAT scheme of the organisation'),
  hours: z.number().min(0).max(100_000).nullable().optional(),
};

export const createLine = defineAction({
  name: 'proposal.line.create',
  method: 'post',
  path: '/proposal/versions/{versionId}/lines',
  permission: P.keys.update,
  input: z.object({ versionId: Id, ...lineFields }).strict(),
  output: ProposalVersion,
  summary: 'Add a line to a draft version: an item, an option, or an alternative in a group',
});

export const updateLine = defineAction({
  name: 'proposal.line.update',
  method: 'patch',
  path: '/proposal/lines/{id}',
  permission: P.keys.update,
  input: z.object({ id: Id, ...updateFields(lineFields) } as { id: typeof Id } & { [K in keyof typeof lineFields]: z.ZodOptional<(typeof lineFields)[K]> }).strict(),
  output: ProposalVersion,
});

export const deleteLine = defineAction({
  name: 'proposal.line.delete',
  method: 'delete',
  path: '/proposal/lines/{id}',
  permission: P.keys.update,
  input: z.object({ id: Id }),
  output: ProposalVersion,
  kind: 'destructive',
});

export const reorderLines = defineAction({
  name: 'proposal.line.reorder',
  method: 'post',
  path: '/proposal/versions/{versionId}/lines/order',
  permission: P.keys.update,
  input: z.object({ versionId: Id, lineIds: z.array(Id).min(1).max(500) }).strict(),
  output: ProposalVersion,
  successStatus: 200,
});

export const attachPdf = defineAction({
  name: 'proposal.version.attachPdf',
  method: 'put',
  path: '/proposal/versions/{id}/pdf',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      contentBase64: z.string().min(1).max(20_000_000).optional().describe('The PDF from the proposal-builder, base64; or fileId'),
      fileId: Id.optional().describe('A PDF uploaded with proposal.version.beginUpload and a PUT (CO-1362); or contentBase64'),
      fileName: z.string().trim().min(1).max(200).optional(),
    })
    .strict(),
  output: ProposalVersion,
  successStatus: 200,
  summary: 'Put the rendered PDF (from the proposal-builder) on a draft version',
  description:
    'At most 15 MB. The web app uploads the file itself through the same route with a PDF body. ' +
    'Above about 50 KB use proposal.version.beginUpload, PUT the bytes to the uploadUrl and give the fileId here: the bytes then never pass through the tool call (CO-1362).',
});

export const beginPdfUpload = defineAction({
  name: 'proposal.version.beginUpload',
  method: 'post',
  path: '/proposal/versions/{id}/uploads',
  permission: P.keys.update,
  input: UploadSpec.extend({ id: Id }).strict(),
  output: UploadTicket,
  summary: 'Start an upload of the PDF of a draft version without base64 (CO-1362): a one-time PUT URL for the raw bytes',
});

export const recordDecision = defineAction({
  name: 'proposal.version.recordDecision',
  method: 'post',
  path: '/proposal/versions/{id}/decision',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      decision: z.enum(['accepted', 'rejected']),
      decidedOn: IsoDate.optional().describe('Default today'),
      selectedLineIds: z.array(Id).max(500).optional().describe('accepted: the options and alternatives the client chose; default what was chosen'),
      note: z.string().max(2000).optional(),
    })
    .strict(),
  output: ProposalDetail,
  successStatus: 200,
  summary: 'Record that the client accepted or rejected a sent version, or a draft version that reached the client outside the app (#317); acceptance wins the lead (CO-0594)',
});

export const sendVersion = defineAction({
  name: 'proposal.version.send',
  method: 'post',
  path: '/proposal/versions/{id}/send',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      to: z.array(z.email()).min(1).max(10).optional().describe('Default: the contact of the proposal'),
      cc: z.array(z.email()).max(10).optional(),
      message: z.string().max(4000).optional().describe('A line of your own above the standard text, for this send only: it replaces the own mail of the version'),
    })
    .strict(),
  output: z.object({ workflowId: z.string(), jobType: z.string(), status: z.string(), joined: z.boolean() }),
  successStatus: 200,
  summary: 'Send the draft version with its PDF: a mail draft in the mailbox of the owner, sent after a person approves it (kind proposal-mail); starts the follow-up (CO-1350)',
  description:
    'Needs the PDF and at least one line. The version is frozen while it waits (sending); a rejection gives it back as draft. ' +
    'With an own mail on the version (CO-1364) that is the mail: its subject, its markdown body as the whole text, its recipients; to, cc and message override it for this send. ' +
    'A send that failed gives the version back as draft with sendError (#273); sending it again with the same content, recipients and message sends the approved draft without a new approval, otherwise it makes a new draft that waits for a new approval.',
});

export const previewMail = defineAction({
  name: 'proposal.version.previewMail',
  method: 'get',
  path: '/proposal/versions/{id}/mail-preview',
  permission: P.keys.read,
  input: z.object({ id: Id }),
  output: z.object({
    custom: z.boolean().describe('true: the own mail of the version; false: the standard text'),
    subject: z.string(),
    to: z.array(z.string()),
    cc: z.array(z.string()),
    html: z.string().describe('The body as the client gets it'),
    text: z.string(),
    attachments: z.array(z.object({ name: z.string() })),
    warnings: z.array(MailWarning),
  }),
  summary: 'The mail of a version as the approver and the client see it: subject, recipients, body, attachments and warnings (CO-1364)',
});

export const draftFollowUp = defineAction({
  name: 'proposal.version.draftFollowUp',
  method: 'post',
  path: '/proposal/versions/{id}/follow-up-draft',
  permission: P.keys.update,
  input: z
    .object({
      id: Id,
      text: z.string().trim().min(1).max(20_000).describe('The reminder, written by an agent or a person'),
      subject: z.string().trim().min(1).max(300).optional().describe('Default: the number and title of the proposal'),
    })
    .strict(),
  output: z.object({ draftId: z.string(), webLink: z.string().nullable() }),
  successStatus: 200,
  summary: 'Put a reminder about a sent proposal, with its PDF, as a mail draft in the mailbox of the owner; a person sends it (CO-1350)',
});

// ---------- events ----------

export const proposalChanged = defineEvent({
  type: 'proposal.proposal.changed',
  version: 1,
  payload: z.object({ proposalId: Id, organisationId: Id, change: z.enum(['created', 'updated', 'version', 'lines', 'pdf', 'cancelled', 'decided']) }),
});

const Outcome = z.object({ proposalId: Id, versionId: Id, organisationId: Id, leadId: Id.nullable(), totalCents: z.number().int(), currency: z.string() });

export const proposalVersionSent = defineEvent({
  type: 'proposal.version.sent',
  version: 1,
  payload: z.object({ proposalId: Id, versionId: Id, organisationId: Id, leadId: Id.nullable(), validUntil: IsoDate }),
});

export const followUpDue = defineEvent({
  type: 'proposal.version.followUpDue',
  version: 1,
  payload: z.object({ proposalId: Id, versionId: Id, organisationId: Id, step: z.enum(['day5', 'day12', 'beforeExpiry']), validUntil: IsoDate }),
});

export const proposalAccepted = defineEvent({ type: 'proposal.proposal.accepted', version: 1, payload: Outcome });
export const proposalRejected = defineEvent({ type: 'proposal.proposal.rejected', version: 1, payload: Outcome });
export const proposalExpired = defineEvent({ type: 'proposal.proposal.expired', version: 1, payload: Outcome });

export const proposalActions = [
  listProposals,
  getProposal,
  createProposal,
  updateProposal,
  cancelProposal,
  createVersion,
  updateVersion,
  createLine,
  updateLine,
  deleteLine,
  reorderLines,
  attachPdf,
  sendVersion,
  draftFollowUp,
  recordDecision,
];
export const proposalEvents = [proposalChanged, proposalVersionSent, followUpDue, proposalAccepted, proposalRejected, proposalExpired];
